import { IconFileCheck } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { BenefitDocumentType } from '@zorro/clients';
import { calculateFamilyUnit } from '@zorro/types';
import { Stack } from '@zorro/web/design-system';
import {
  showErrorNotification,
  useFlowId,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { Controller } from 'react-hook-form';

import { useAnalytics } from '../../Analytics';
import {
  UploadableBenefitDocument,
  usePersistUploadedDocuments,
} from '../../BenefitDocuments';
import {
  EnrollmentReviewAllowanceTooltip,
  EnrollmentReviewCardContent,
} from '../../EnrollmentReviewCard';
import { HeaderWithIcon } from '../../HeaderWithIcon';
import { ProofOfConsentSection } from '../../ProofOfConsent';
import {
  ManualSubmissionStep,
  ManualSubmissionStepSubmit,
  ManualSubmissionStepType,
} from '../ManualSubmissionStep';
import { ManualElectionLegalConsent } from './ManualElectionLegalConsent';
import { useManualElectionConsent } from './useManualElectionConsent';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  onClose: () => void;
  isDon?: boolean;
};

type ProofOfConsentFormFields = {
  proofOfConsentDocuments: UploadableBenefitDocument[];
};

export const ManualSubmissionReviewStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  onClose,
  isDon = false,
}: Props) => {
  const queryClient = useQueryClient();
  const { get: getIdempotencyKey } = useIdempotencyKey();
  const { sendEvent } = useAnalytics();
  const flowId = useFlowId();

  const consent = useManualElectionConsent(onboardingPeriodId, employeeId);
  const { authUserRoles } = useRoles();
  // Same query as the medical plan step, so it is served from the cache.
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const { data: onboardingPeriod, isLoading: isLoadingOnboardingPeriod } =
    useMonolithQuery({
      method: 'onboardingPeriodsControllerFindOne',
      params: [employeeId, onboardingPeriodId],
    });

  const previousProofOfConsentDocuments = (
    onboardingPeriod?.benefitDocuments ?? []
  ).filter(({ type }) => type === BenefitDocumentType.ELECTION_CONSENT);

  const proofOfConsentForm = useForm<ProofOfConsentFormFields>({
    defaultValues: { proofOfConsentDocuments: previousProofOfConsentDocuments },
  });
  const persistDocuments = usePersistUploadedDocuments({
    employerId,
    employeeId,
    onboardingPeriodId,
    documentType: BenefitDocumentType.ELECTION_CONSENT,
    documentLabel: 'proof of consent',
  });

  const { tryMutate } = useMonolithMutation({
    method: 'onboardingPeriodsControllerSubmitBenefitsElection',
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
  });

  const sendSubmitFailedEvent = async (reason: string) => {
    await sendEvent('manual_election_submit_failed', {
      onboarding_period_id: onboardingPeriodId,
      reason,
    });
  };

  const handleStepSubmit: ManualSubmissionStepSubmit = async () => {
    const electronicSignature = await consent.getElectronicSignature();
    if (!electronicSignature) {
      showErrorNotification();
      await sendSubmitFailedEvent('missing_signature');
      return { navigate: false };
    }

    // Before the election so a failed upload can be retried on the same step -
    // submitting closes the modal and the picked files would be lost.
    const persistResult = await persistDocuments(
      proofOfConsentForm.getValues('proofOfConsentDocuments')
    );
    proofOfConsentForm.setValue(
      'proofOfConsentDocuments',
      persistResult.documents
    );
    if (!persistResult.isSuccess) {
      await sendSubmitFailedEvent('proof_of_consent_upload_failed');
      return { navigate: false };
    }

    const result = await tryMutate(
      [onboardingPeriodId, getIdempotencyKey(), { electronicSignature }],
      { flowId }
    );

    if (result.isErr()) {
      await sendSubmitFailedEvent('submission_failed');
      return { navigate: false };
    }

    const insuredPeople = benefits?.majorMedicalBenefit?.insuredPeople;
    await sendEvent('election_submitted', {
      onboarding_period_id: onboardingPeriodId,
      plan_id: consent.reviewSnapshot?.payload.plan?.externalID,
      user_roles: authUserRoles,
      is_self_reported: false,
      review_snapshot_id: electronicSignature.reviewSnapshotId,
      family_unit: insuredPeople && calculateFamilyUnit(insuredPeople),
      proof_of_consent_document_count: persistResult.documents.length,
    });

    setTimeout(async () => {
      await queryClient.invalidateQueries();
    }, 0);
    onClose();
    return { navigate: true };
  };

  return (
    <ManualSubmissionStep
      step={ManualSubmissionStepType.REVIEW}
      onboardingPeriodId={onboardingPeriodId}
      submitLabel="Submit election"
      onSubmit={handleStepSubmit}
      isSubmitDisabled={!consent.isSigned || !consent.reviewSnapshot}
      employerId={employerId}
      employeeId={employeeId}
      isDon={isDon}
    >
      <Stack w="100%" gap="2.5rem">
        <Stack w="100%">
          <HeaderWithIcon
            icon={IconFileCheck}
            title="Review & confirm coverage"
          />
          {consent.reviewSnapshot && (
            <EnrollmentReviewCardContent
              stipend={consent.reviewSnapshot.payload.stipend}
              totals={consent.reviewSnapshot.payload.enrollmentReview}
              allowanceTooltip={
                <EnrollmentReviewAllowanceTooltip
                  onboardingPeriodId={onboardingPeriodId}
                />
              }
            />
          )}
        </Stack>
        <ManualElectionLegalConsent employeeId={employeeId} consent={consent} />
        {/* Held back with the rest of the step - the snapshot gates everything
            above, so an ungated dropzone would render on its own first. */}
        {consent.reviewSnapshot && !isLoadingOnboardingPeriod && (
          <Controller
            control={proofOfConsentForm.control}
            name="proofOfConsentDocuments"
            render={({ field: { value, onChange } }) => (
              <ProofOfConsentSection
                onboardingPeriodId={onboardingPeriodId}
                documents={value}
                subtitle="Attach a file documenting the employee's consent, if you have one."
                dropzoneTitle="Upload a file"
                onChange={onChange}
              />
            )}
          />
        )}
      </Stack>
    </ManualSubmissionStep>
  );
};
